import { Button } from "@bcc/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bcc/components/ui/dialog";
import { Separator } from "@bcc/components/ui/separator";
import { FileText, MapPin, Package, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setOrderDetailDialogTestId } from "./order-detail-dialog.utils";

interface Order {
  id: string;
  customer: string;
  product: string;
  quantity: number;
  weight: number;
  amount: string;
  status: string;
  date: string;
  marketplace: string;
  tracking: string | null;
  batch: string | null;
}

interface OrderDetailDialogProps {
  order: Order | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId?: string;
}

export function OrderDetailDialog({
  order,
  open,
  onOpenChange,
  testId,
}: OrderDetailDialogProps) {
  const { t } = useTranslation();

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        testId={testId ?? setOrderDetailDialogTestId("content")}
        className="sm:max-w-[600px]"
      >
        <DialogHeader>
          <DialogTitle>{t("orders.orderDetail.title")}</DialogTitle>
          <DialogDescription>
            {t("orders.orderDetail.order", { id: order.id })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Shipping Address */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <MapPin className="h-4 w-4" />
              {t("orders.orderDetail.shippingAddress")}
            </div>
            <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm">
              <div className="font-medium">{order.customer}</div>
              <div className="text-muted-foreground">123 Coffee Street</div>
              <div className="text-muted-foreground">Seattle, WA 98101</div>
              <div className="text-muted-foreground">United States</div>
            </div>
          </div>

          <Separator />

          {/* Product Details */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Package className="h-4 w-4" />
              {t("orders.orderDetail.productDetails")}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("orders.orderDetail.product")}
                </span>
                <span className="font-medium">{order.product}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("orders.orderDetail.quantity")}
                </span>
                <span className="font-medium">
                  {order.quantity} {t("orders.orderDetail.bags")}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("orders.orderDetail.weight")}
                </span>
                <span className="font-medium">{order.weight} oz</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("orders.orderDetail.grind")}
                </span>
                <span className="font-medium">
                  {t("orders.orderDetail.wholeBean")}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between text-sm font-semibold">
                <span>{t("orders.orderDetail.total")}</span>
                <span>{order.amount}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Order Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">
                {t("orders.orderDetail.marketplace")}
              </div>
              <div className="font-medium">{order.marketplace}</div>
            </div>
            <div>
              <div className="text-muted-foreground">
                {t("orders.orderDetail.orderDate")}
              </div>
              <div className="font-medium">{order.date}</div>
            </div>
            {order.batch && (
              <div>
                <div className="text-muted-foreground">
                  {t("orders.orderDetail.batch")}
                </div>
                <div className="font-medium font-mono">{order.batch}</div>
              </div>
            )}
            {order.tracking && (
              <div>
                <div className="text-muted-foreground">
                  {t("orders.orderDetail.tracking")}
                </div>
                <div className="font-medium font-mono text-xs">
                  {order.tracking}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button className="flex-1">
              <Printer className="mr-2 h-4 w-4" />
              {t("orders.orderDetail.printLabel")}
            </Button>
            <Button variant="outline" className="flex-1 bg-transparent">
              <FileText className="mr-2 h-4 w-4" />
              {t("orders.orderDetail.packingSlip")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
